import type { Envelope, HostCommand } from "./types";

export type HostWsState = "connecting" | "open" | "closed";

export interface HostWsOptions {
  room?: string; // omit for a fresh host (server creates the room)
  url?: string; // override base; defaults to ws(s)://<location.host>/host
  wsFactory?: (url: string) => WebSocketLike;
  baseDelay?: number;
  maxDelay?: number;
}

export interface WebSocketLike {
  readyState: number;
  onopen: (() => void) | null;
  onmessage: ((e: { data: string }) => void) | null;
  onclose: (() => void) | null;
  onerror: (() => void) | null;
  close(): void;
  send(data: string): void;
}

const OPEN = 1;

export class HostClient {
  private ws: WebSocketLike | null = null;
  private state: HostWsState = "connecting";
  private attempt = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private closedByUser = false;
  private msgCb: ((env: Envelope) => void) | null = null;
  private stateCb: ((s: HostWsState) => void) | null = null;
  private readonly url: string;
  private readonly factory: (url: string) => WebSocketLike;
  private readonly baseDelay: number;
  private readonly maxDelay: number;

  constructor(opts: HostWsOptions) {
    const base = opts.url ?? defaultHostBase();
    // A fresh host omits the room; the server creates one and replies
    // `room-created`. A reconnecting host passes the known room code.
    this.url = opts.room
      ? `${base}/host?room=${encodeURIComponent(opts.room)}`
      : `${base}/host`;
    this.factory = opts.wsFactory ?? ((u) => new WebSocket(u) as unknown as WebSocketLike);
    this.baseDelay = opts.baseDelay ?? 1000;
    this.maxDelay = opts.maxDelay ?? 30000;
  }

  onMessage(cb: (env: Envelope) => void) {
    this.msgCb = cb;
  }
  onState(cb: (s: HostWsState) => void) {
    this.stateCb = cb;
  }
  get connected() {
    return this.state;
  }

  open() {
    this.closedByUser = false;
    this.connect();
  }

  close() {
    this.closedByUser = true;
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    this.ws?.close();
    this.ws = null;
    this.setState("closed");
  }

  send(cmd: HostCommand) {
    if (this.ws && this.ws.readyState === OPEN) {
      this.ws.send(JSON.stringify(cmd));
    }
  }

  private connect() {
    this.setState("connecting");
    const ws = this.factory(this.url);
    this.ws = ws;
    ws.onopen = () => {
      this.attempt = 0;
      this.setState("open");
    };
    ws.onmessage = (e) => {
      try {
        this.msgCb?.(JSON.parse(e.data) as Envelope);
      } catch {
        /* drop malformed */
      }
    };
    ws.onclose = () => {
      this.ws = null;
      this.setState("closed");
      this.scheduleReconnect();
    };
    ws.onerror = () => {
      this.setState("closed");
    };
  }

  private scheduleReconnect() {
    if (this.closedByUser) return;
    const delay = Math.min(this.maxDelay, this.baseDelay * 2 ** this.attempt);
    this.attempt += 1;
    this.timer = setTimeout(() => this.connect(), delay);
  }

  private setState(s: HostWsState) {
    this.state = s;
    this.stateCb?.(s);
  }
}

export function defaultHostBase(): string {
  const proto = location.protocol === "https:" ? "wss" : "ws";
  return `${proto}://${location.host}`;
}